import {
  AgeRangeFilter,
  CheckboxFilter,
  DateFilter,
  Filter,
  FilterType,
  NumericFilter,
  SalesDataFilter,
  SearchQueryFilters,
} from "~/hooks/search/types";
import { getActiveSalesDataMetrics } from "~/hooks/search/sales-data-filter-utils";
import { isValidFilter } from "~/pages/books/components/filters/utils";
import { FILTER_NAME_MAP, getCanonicalFilterName } from "./canonical-names";

export interface AppliedDiffEntry {
  filter_name: string;
  /** `"range"` for date ranges, otherwise the literal value applied. */
  filter_value: string;
}

export interface RemovedDiffEntry {
  filter_name: string;
}

export interface FiltersDiff {
  applied: AppliedDiffEntry[];
  removed: RemovedDiffEntry[];
}

const isCheckbox = (
  filter: Filter | null | undefined,
): filter is CheckboxFilter =>
  Boolean(filter && filter.type === FilterType.CHECKBOX);

const isNumeric = (
  filter: Filter | null | undefined,
): filter is NumericFilter =>
  Boolean(filter && filter.type === FilterType.NUMERIC);

const isDateRange = (filter: Filter | null | undefined): filter is DateFilter =>
  Boolean(filter && filter.type === FilterType.DATE_RANGE);

const isAgeRange = (
  filter: Filter | null | undefined,
): filter is AgeRangeFilter =>
  Boolean(filter && filter.type === FilterType.AGE_RANGE);

const isSalesData = (
  filter: Filter | null | undefined,
): filter is SalesDataFilter =>
  Boolean(filter && filter.type === FilterType.SALES_DATA);

const valueOrEmpty = (filter: Filter | null | undefined): string[] =>
  isCheckbox(filter) ? filter.values : [];

// Stricter than isValidFilter: empty date ranges and full-default age ranges count as inactive so clearing still diffs.
const isActiveForDiff = (
  filter: Filter | null | undefined,
  salesDataMaxDate: Date,
): boolean => {
  if (!filter) return false;
  if (isDateRange(filter) && !filter.range) return false;
  return isValidFilter(filter, salesDataMaxDate);
};

// Asymmetric diff: applies per checkbox value; removals per key (including partial deselect).
export const diffFilters = (
  prev: SearchQueryFilters,
  next: SearchQueryFilters,
): FiltersDiff => {
  const applied: AppliedDiffEntry[] = [];
  const removed: RemovedDiffEntry[] = [];
  const salesDataMaxDate = new Date();

  (Object.keys(FILTER_NAME_MAP) as (keyof SearchQueryFilters)[]).forEach(
    (key) => {
      const prevFilter = prev[key];
      const nextFilter = next[key];
      const filterName = getCanonicalFilterName(key);

      const prevValid = isActiveForDiff(prevFilter, salesDataMaxDate);
      const nextValid = isActiveForDiff(nextFilter, salesDataMaxDate);

      if (prevValid && !nextValid) {
        removed.push({ filter_name: filterName });
        return;
      }

      if (!nextValid) return;

      if (isCheckbox(nextFilter)) {
        const prevValues = new Set(valueOrEmpty(prevFilter));
        const nextValues = new Set(nextFilter.values);
        nextFilter.values.forEach((value) => {
          if (!prevValues.has(value)) {
            applied.push({ filter_name: filterName, filter_value: value });
          }
        });
        const hasDeselection = Array.from(prevValues).some(
          (value) => !nextValues.has(value),
        );
        if (hasDeselection) {
          removed.push({ filter_name: filterName });
        }
        return;
      }

      if (isNumeric(nextFilter) && nextFilter.value !== undefined) {
        const prevValue = isNumeric(prevFilter) ? prevFilter.value : undefined;
        if (prevValue !== nextFilter.value) {
          applied.push({
            filter_name: filterName,
            filter_value: String(nextFilter.value),
          });
        }
        return;
      }

      if (isDateRange(nextFilter) && nextFilter.range) {
        const prevRange = isDateRange(prevFilter)
          ? prevFilter.range
          : undefined;
        const changed =
          prevRange?.from !== nextFilter.range.from ||
          prevRange?.to !== nextFilter.range.to;
        if (changed) {
          applied.push({ filter_name: filterName, filter_value: "range" });
        }
        return;
      }

      if (isAgeRange(nextFilter)) {
        const prevAge = isAgeRange(prevFilter) ? prevFilter : undefined;
        const ageTypeChanged = prevAge?.ageType !== nextFilter.ageType;
        const boundsChanged =
          prevAge?.range?.min !== nextFilter.range.min ||
          prevAge?.range?.max !== nextFilter.range.max;
        if (ageTypeChanged || boundsChanged) {
          applied.push({
            filter_name: filterName,
            filter_value: nextFilter.ageType,
          });
        }
      }

      if (isSalesData(nextFilter)) {
        const prevSalesData = isSalesData(prevFilter) ? prevFilter : undefined;
        const prevActiveMetrics = getActiveSalesDataMetrics(prevSalesData);
        const nextActiveMetrics = getActiveSalesDataMetrics(nextFilter);
        const prevMetrics = new Map(
          prevActiveMetrics.map((metric) => [metric.key, metric]),
        );
        const nextMetrics = new Map(
          nextActiveMetrics.map((metric) => [metric.key, metric]),
        );
        const changedMetrics = nextActiveMetrics.filter((metric) => {
          const prevMetric = prevMetrics.get(metric.key);
          return (
            prevMetric?.value !== metric.value ||
            prevMetric?.comparisonOperator !== metric.comparisonOperator
          );
        });
        const hasRemovedMetric = prevActiveMetrics.some(
          (metric) => !nextMetrics.has(metric.key),
        );
        const rangeChanged =
          prevActiveMetrics.length > 0 &&
          (prevSalesData?.range.from !== nextFilter.range.from ||
            prevSalesData?.range.to !== nextFilter.range.to);

        changedMetrics.forEach((metric) => {
          applied.push({
            filter_name: filterName,
            filter_value: `${metric.definition.analyticsValue}:${metric.comparisonOperator}:${metric.value}`,
          });
        });

        if (hasRemovedMetric) {
          removed.push({ filter_name: filterName });
        }

        if (rangeChanged) {
          applied.push({
            filter_name: filterName,
            filter_value: "range",
          });
        }
      }
    },
  );

  return { applied, removed };
};
